import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import MobilePage from '../UiComponents/MobilePage';
import { isLogin } from '../Utils/Cookies';
import {
  CATEGORY_LABELS,
  STATUS_LABELS,
  formatInquiryDate,
  getApiErrorMessage,
} from './supportInquiryUtils';
import './SupportInquiry.css';
import './SupportInquiryDetail.css';

const SupportInquiryDetail = () => {
  const { inquiryNo } = useParams();
  const [inquiry, setInquiry] = useState()
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  const load = useCallback(async () => {
    if (!isLogin()) {
      setLoading(false)
      return
    }

    setLoading(true)
    setErrorMessage('')
    const response = await butterflyApi.getSupportInquiry(inquiryNo, error => {
      setErrorMessage(getApiErrorMessage(error, '문의 내용을 불러오지 못했습니다.'))
    })
    setInquiry(response)
    setLoading(false)
  }, [inquiryNo])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    load()
  }, [load])

  if (loading) {
    return <MobilePage className='storybook-page inquiry-page inquiry-detail-page' loading loadingMessage='문의 내용을 불러오고 있습니다.' />
  }

  const loggedIn = Boolean(isLogin())

  if (!loggedIn || !inquiry) {
    return <MobilePage className='storybook-page inquiry-page inquiry-detail-page'>
      <main className='inquiry-content'>
        <Link className='book-text-link inquiry-back' to='/support/inquiries'><span aria-hidden='true'>‹</span> 문의 내역</Link>
        <header className='inquiry-intro'>
          <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 남겨주신 이야기에 귀 기울일게요</p>
          <div className='inquiry-title-row'>
            <h1 className='book-title'>문의 상세</h1>
            <span className='inquiry-intro-icon' aria-hidden='true'>?</span>
          </div>
          <p className='book-description'>내가 남긴 문의와<br />텔링파파의 답변을 확인하는 곳이에요.</p>
        </header>
        <section className='inquiry-empty' aria-labelledby='inquiry-detail-empty-title'>
          <span className='inquiry-empty-icon' aria-hidden='true'>✧</span>
          <h2 id='inquiry-detail-empty-title'>{loggedIn ? '문의 내용을 찾을 수 없어요' : '로그인하고 답변을 확인해 주세요'}</h2>
          <p>{loggedIn ? '문의 내역에서 확인할 내용을 다시 선택해 주세요.' : '로그인하면 내가 남긴 문의와 답변을 확인할 수 있어요.'}</p>
          {loggedIn && errorMessage && <div className='book-error inquiry-error' role='alert'>{errorMessage}</div>}
          <Link
            className='book-primary-button'
            to={loggedIn ? '/support/inquiries' : `/login?redirect=${encodeURIComponent(`/support/inquiries/${inquiryNo}`)}`}
          >
            {loggedIn ? '문의 내역으로 돌아가기' : '로그인하기'} <span aria-hidden='true'>→</span>
          </Link>
        </section>
      </main>
    </MobilePage>
  }

  return <MobilePage className='storybook-page inquiry-page inquiry-detail-page'>
    <main className='inquiry-content'>
      <Link className='book-text-link inquiry-back' to='/support/inquiries'><span aria-hidden='true'>‹</span> 문의 내역</Link>

      <header className='inquiry-intro inquiry-detail-intro'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 1:1 문의 · 문의 상세</p>
        <div className='inquiry-detail-meta'>
          <span className='inquiry-category'>{CATEGORY_LABELS[inquiry.category] || inquiry.category}</span>
          <span className='inquiry-status' data-status={inquiry.status}>
            <span className='inquiry-status-dot' aria-hidden='true' />{STATUS_LABELS[inquiry.status] || inquiry.status}
          </span>
        </div>
        <h1 className='book-title'>{inquiry.title}</h1>

        <dl className='inquiry-detail-info'>
          <div>
            <dt>접수 일시</dt>
            <dd>{inquiry.registerDatetime ? <time dateTime={inquiry.registerDatetime.replace(' ', 'T')}>{formatInquiryDate(inquiry.registerDatetime)}</time> : '—'}</dd>
          </div>
          {inquiry.heroNo && <div>
            <dt>관련 주인공 번호</dt>
            <dd>{inquiry.heroNo}</dd>
          </div>}
        </dl>
      </header>

      <section className='inquiry-detail-panel' aria-labelledby='inquiry-question-title'>
        <div className='inquiry-detail-panel-heading'>
          <span className='inquiry-detail-letter' aria-hidden='true'>Q</span>
          <div>
            <h2 id='inquiry-question-title'>문의 내용</h2>
            <p>내가 남긴 이야기</p>
          </div>
        </div>
        <p className='inquiry-detail-body'>{inquiry.content}</p>
      </section>

      <section
        className={`inquiry-detail-panel inquiry-detail-answer${inquiry.answerContent ? ' inquiry-detail-answer--ready' : ''}`}
        aria-labelledby='inquiry-answer-title'
      >
        <div className='inquiry-detail-panel-heading'>
          <span className='inquiry-detail-letter' aria-hidden='true'>A</span>
          <div>
            <h2 id='inquiry-answer-title'>텔링파파의 답변</h2>
            {inquiry.answeredDatetime && <p>
              답변 일시 <time dateTime={inquiry.answeredDatetime.replace(' ', 'T')}>{formatInquiryDate(inquiry.answeredDatetime)}</time>
            </p>}
          </div>
        </div>
        {inquiry.answerContent ? <p className='inquiry-detail-body'>{inquiry.answerContent}</p> : <div className='inquiry-detail-pending' data-status={inquiry.status}>
          <span aria-hidden='true'>{inquiry.status === 'CLOSED' ? '✓' : '✧'}</span>
          <h3>{inquiry.status === 'CLOSED' ? '문의가 종료되었어요' : inquiry.status === 'WAITING' ? '답변을 준비하고 있어요' : '아직 등록된 답변이 없어요'}</h3>
          <p>{inquiry.status === 'CLOSED' ? <>추가로 도움이 필요하시면<br />새 문의를 남겨주세요.</> : <>남겨주신 내용을 확인하고 답변드릴게요.<br />문의 내역에서 다시 확인해 주세요.</>}</p>
        </div>}
      </section>

      <nav className='inquiry-detail-actions' aria-label='문의 메뉴'>
        <Link className='book-primary-button' to='/support/inquiries'>문의 내역으로 돌아가기 <span aria-hidden='true'>→</span></Link>
        <Link className='book-text-link' to='/support/inquiries/new'>새 문의 작성하기 <span aria-hidden='true'>＋</span></Link>
      </nav>
      <p className='inquiry-footnote'><span aria-hidden='true'>✦</span> 작은 궁금함도 편하게 이야기해 주세요.</p>
    </main>
  </MobilePage>
}

export default SupportInquiryDetail
